import { Component } from '@theme/component';
import { preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Line item property used to split an add-to-cart submission between what's
// immediately available and what's on backorder, so both the customer and
// fulfillment can see which part of the order ships now vs later. The split
// point itself (`data-fast-quantity-threshold`) is computed server-side,
// since it depends on the signed-in customer and a variant metafield.
//
// DELIVERY_TIME_FAST is the only thing that changed here: the line item
// property value sent to Shopify is now "Instant" instead of "Fast". All the
// surrounding "fast"-named logic (fastQuantity, alreadyFastQuantity,
// data-fast-quantity-threshold, etc.) is unchanged and still means the same
// thing — only this one outward-facing string differs, and every comparison
// against it already goes through this constant, so nothing else needs to
// change for that logic to keep working.
const DELIVERY_TIME_PROPERTY = 'Delivery Time';
const DELIVERY_TIME_FAST = 'Instant';
const DELIVERY_TIME_SLOW = '9-14 days';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} stockNoteCartCount - The "(N in cart)" element inside the stock note.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLElement | undefined} productNoteCallout - The always-rendered outer wrapper morphed wholesale on variant change; contains both persistentNoteCallout and deliverySplitNote.
 * @property {HTMLElement | undefined} persistentNoteCallout - Inner wrapper around the staff/guest/stock/backorder/"contact us" note callouts — shown/hidden independently of deliverySplitNote, its sibling.
 * @property {HTMLElement | undefined} deliverySplitNote - The always-rendered, JS-controlled fast/slow split preview note.
 * @property {HTMLElement | undefined} deliverySplitNoteText - The text element inside `deliverySplitNote`.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** Guards against a slower, stale #updateDeliverySplitNote() call overwriting a newer one. */
  #deliverySplitNoteRequestId = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Keep the fast/slow split preview note current as the shopper adjusts
    // the quantity selector (this bubbles up from quantity-selector-component).
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, { signal });

    this.#updateDeliverySplitNote();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }

    // The cart changed from elsewhere (another tab, the cart drawer, etc.) —
    // re-check whether this variant's "Fast" capacity is still available.
    this.#updateDeliverySplitNote();
  };

  /** Recomputes the fast/slow split preview note when the quantity selector changes. */
  #onQuantitySelectorUpdate = () => {
    this.#updateDeliverySplitNote();
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Shows/updates/hides the product-page preview of how the currently
   * selected quantity would split between "Fast" and "Slow" if added to
   * cart right now — accounting for however much of this variant's "Fast"
   * capacity is already claimed by the cart. Hidden whenever nothing is
   * available at all (the backorder/contact/staff notes already cover that)
   * or when the current selection doesn't exceed what's left.
   */
  async #updateDeliverySplitNote() {
    const { deliverySplitNote, deliverySplitNoteText, persistentNoteCallout, variantId } = this.refs;
    if (!deliverySplitNote || !deliverySplitNoteText) return;

    // If a newer call starts while this one is still awaiting the cart fetch
    // below, let the newer one win — bail out once it resolves.
    const requestId = ++this.#deliverySplitNoteRequestId;

    const threshold = Math.max(0, Number(this.dataset.fastQuantityThreshold) || 0);
    const currentVariantId = variantId?.value;

    if (threshold <= 0 || !currentVariantId) {
      deliverySplitNote.classList.add('hidden');
      // Never suppressed by this early-out — the persistent note (staff/
      // stock/backorder/contact) never co-occurs with the split note here,
      // since a zero/negative threshold already hides the split note too.
      persistentNoteCallout?.classList.remove('hidden');
      return;
    }

    const quantity = this.#getQuantity();
    const alreadyFastQuantity = await this.#getCartFastQuantity(currentVariantId);
    if (requestId !== this.#deliverySplitNoteRequestId) return;

    const remainingFastCapacity = Math.max(0, threshold - alreadyFastQuantity);
    const fastQuantity = Math.min(quantity, remainingFastCapacity);
    const slowQuantity = quantity - fastQuantity;

    if (slowQuantity <= 0) {
      deliverySplitNote.classList.add('hidden');
      persistentNoteCallout?.classList.remove('hidden');
      return;
    }

    // innerHTML, not textContent: the template (from the theme's own locale
    // file, not shopper input) contains trusted markup — <strong>/<a> — that
    // needs to actually render rather than show up as literal tag text. Only
    // the plain numeric quantities (and the unit words below) are
    // substituted in.
    const template = this.dataset.deliverySplitNoteTemplate || '';
    deliverySplitNoteText.innerHTML = template
      .replace('{{ quantity }}', quantity.toString())
      .replace('{{ fast }}', fastQuantity.toString())
      .replace('{{ fast_unit }}', this.#bunchUnitFor(fastQuantity))
      .replace('{{ slow }}', slowQuantity.toString())
      .replace('{{ slow_unit }}', this.#bunchUnitFor(slowQuantity));

    deliverySplitNote.classList.remove('hidden');
    // Only one note at a time: the split note takes priority over whatever
    // persistent note (staff/stock view) would otherwise show for this
    // variant — showing both is redundant and, worse, "5 in stock" reads as
    // contradicting a note that's telling the shopper part of their order
    // is backordered.
    persistentNoteCallout?.classList.add('hidden');
  }

  /**
   * Pluralized "bunch"/"bunches" word for a delivery-split quantity,
   * including its own leading space so it can be appended directly after
   * the number in the template — empty string (no leading space either)
   * for variants without the Bunch Stems metafield, matching how the note
   * reads today with no unit word at all.
   * @param {number} quantity
   * @returns {string}
   */
  #bunchUnitFor(quantity) {
    const { bunchUnitSingular, bunchUnitPlural } = this.dataset;
    if (!bunchUnitSingular && !bunchUnitPlural) return '';

    const word = quantity === 1 ? bunchUnitSingular ?? bunchUnitPlural : bunchUnitPlural ?? bunchUnitSingular;
    return word ? ` ${word}` : '';
  }

  /**
   * Pulls `properties[Key]` fields (e.g. gift card recipient info) off the
   * submitted form so they can be copied onto every split line item.
   * @param {FormData} formData
   * @returns {Record<string, string>}
   */
  #extractFormProperties(formData) {
    /** @type {Record<string, string>} */
    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') {
        properties[match[1]] = value;
      }
    }

    return properties;
  }

  /**
   * Returns how much of `variantId`'s quantity already sitting in the cart is
   * tagged "Fast", so a repeat add-to-cart only fills whatever fast capacity
   * remains instead of re-granting the full threshold on every click.
   * @param {FormDataEntryValue | string} variantId
   * @returns {Promise<number>}
   */
  async #getCartFastQuantity(variantId) {
    const numericVariantId = Number(variantId);

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return (cart.items ?? [])
        .filter(
          (item) => item.variant_id === numericVariantId && item.properties?.[DELIVERY_TIME_PROPERTY] === DELIVERY_TIME_FAST
        )
        .reduce((sum, item) => sum + item.quantity, 0);
    } catch (error) {
      console.error('Failed to read cart for delivery-time split:', error);
      return 0;
    }
  }

  /**
   * Splits a requested add-to-cart quantity into "fast" (within whatever
   * tracked/available inventory hasn't already been claimed by this same
   * variant's "Fast" lines already in the cart) and "slow" (backordered)
   * cart line items, each tagged with `DELIVERY_TIME_PROPERTY` so the
   * customer and fulfillment can see which part of the order ships now vs
   * later.
   *
   * @param {FormDataEntryValue} variantId
   * @param {number} quantity
   * @param {Record<string, string>} baseProperties - other line item properties already on the form, copied onto both split lines
   * @param {FormDataEntryValue | null} sellingPlan
   * @param {number} [alreadyFastQuantity] - quantity of this variant already in the cart tagged "Fast"
   * @returns {Array<{id: number, quantity: number, properties: Record<string, string>, selling_plan?: string}>}
   */
  #buildDeliveryTimeItems(variantId, quantity, baseProperties, sellingPlan, alreadyFastQuantity = 0) {
    const threshold = Math.max(0, Number(this.dataset.fastQuantityThreshold) || 0);
    const remainingFastCapacity = Math.max(0, threshold - alreadyFastQuantity);
    const fastQuantity = Math.min(quantity, remainingFastCapacity);
    const slowQuantity = quantity - fastQuantity;

    /**
     * @param {number} lineQuantity
     * @param {string} deliveryTime
     */
    const buildItem = (lineQuantity, deliveryTime) => {
      /** @type {{id: number, quantity: number, properties: Record<string, string>, selling_plan?: string}} */
      const item = {
        id: Number(variantId),
        quantity: lineQuantity,
        properties: { ...baseProperties, [DELIVERY_TIME_PROPERTY]: deliveryTime },
      };
      if (typeof sellingPlan === 'string' && sellingPlan) {
        item.selling_plan = sellingPlan;
      }
      return item;
    };

    const items = [];
    if (fastQuantity > 0) items.push(buildItem(fastQuantity, DELIVERY_TIME_FAST));
    if (slowQuantity > 0) items.push(buildItem(slowQuantity, DELIVERY_TIME_SLOW));

    return items;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const variantId = formData.get('id');
    if (!variantId) throw new Error('Form ID is required');

    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const baseProperties = this.#extractFormProperties(formData);
    const sellingPlan = formData.get('selling_plan');
    const alreadyFastQuantity = await this.#getCartFastQuantity(variantId);
    const items = this.#buildDeliveryTimeItems(variantId, quantity, baseProperties, sellingPlan, alreadyFastQuantity);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items, sections: cartItemComponentsSectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          // This variant's "Fast" capacity may now be partly or fully claimed.
          this.#updateDeliverySplitNote();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, variantId.toString(), {
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  async #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    // Track remaining "Fast" capacity per variant as queued items are split, in
    // case more than one queued item shares a variant (each consumes whatever
    // capacity the previous one in this same batch didn't).
    /** @type {Map<string, number>} */
    const fastQuantityByVariant = new Map();
    const builtItems = [];
    for (const item of items) {
      if (!fastQuantityByVariant.has(item.variantId)) {
        fastQuantityByVariant.set(item.variantId, await this.#getCartFastQuantity(item.variantId));
      }

      const alreadyFastQuantity = /** @type {number} */ (fastQuantityByVariant.get(item.variantId));
      const built = this.#buildDeliveryTimeItems(item.variantId, item.quantity, {}, null, alreadyFastQuantity);
      const fastUsed = built.find((builtItem) => builtItem.properties[DELIVERY_TIME_PROPERTY] === DELIVERY_TIME_FAST);

      fastQuantityByVariant.set(item.variantId, alreadyFastQuantity + (fastUsed?.quantity ?? 0));
      builtItems.push(...built);
    }

    const payload = {
      items: builtItems,
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        // This variant's "Fast" capacity may now be partly or fully claimed.
        this.#updateDeliverySplitNote();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates a "(N in cart)"-style element's count and show/hide state,
   * preserving whatever trailing text (e.g. the translated "in cart") the
   * server originally rendered inside the parentheses.
   * @param {HTMLElement | undefined} element
   * @param {number} cartQty
   */
  #updateCartCountElement(element, cartQty) {
    if (!element) return;

    const inCartText = element.textContent?.match(/\((\d+)\s+(.+)\)/);
    if (inCartText && inCartText[2]) {
      element.textContent = `(${cartQty} ${inCartText[2]})`;
    }

    element.classList.toggle('hidden', cartQty === 0);
  }

  /**
   * Updates the quantity label and stock note with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    this.#updateCartCountElement(this.refs.quantityLabelCartCount, cartQty);
    this.#updateCartCountElement(this.refs.stockNoteCartCount, cartQty);
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    // Keep the fast/slow delivery-time split threshold current for the newly
    // selected variant — it depends on server-side data (customer + variant
    // metafield) this component has no other way to know about.
    const newProductFormComponent = event.detail.data.html.querySelector('product-form-component');
    const newFastQuantityThreshold = newProductFormComponent?.getAttribute('data-fast-quantity-threshold');
    if (newFastQuantityThreshold != null) {
      this.dataset.fastQuantityThreshold = newFastQuantityThreshold;
    }

    // Same idea for the "bunch"/"bunches" words used to pluralize the
    // delivery-split note below (#updateDeliverySplitNote) — also
    // per-variant, via the same Bunch Stems metafield.
    const newBunchUnitSingular = newProductFormComponent?.getAttribute('data-bunch-unit-singular');
    if (newBunchUnitSingular) {
      this.dataset.bunchUnitSingular = newBunchUnitSingular;
    } else {
      delete this.dataset.bunchUnitSingular;
    }

    const newBunchUnitPlural = newProductFormComponent?.getAttribute('data-bunch-unit-plural');
    if (newBunchUnitPlural) {
      this.dataset.bunchUnitPlural = newBunchUnitPlural;
    } else {
      delete this.dataset.bunchUnitPlural;
    }

    // Same idea for the quantity selector's unit label ("bunch"/"bunches"/
    // "quantity"), which also varies per variant (Bunch Stems is a
    // per-variant metafield). The individual-morph branch further down
    // never touches this element's own attributes — it only updates the
    // inner input's min/max/step — so without this it would freeze at
    // whatever the page first loaded with. The singular/plural forms are
    // synced too (not just the currently-displayed label) so that
    // updateButtonStates() -> updateUnitLabel() keeps switching between
    // "1 bunch" and "{quantity} bunches" correctly for the new variant as
    // the shopper keeps changing the quantity.
    if (this.refs.quantitySelector) {
      const newQuantitySelector = event.detail.data.html.querySelector('[ref="quantitySelector"]');
      const newUnitLabel = newQuantitySelector?.getAttribute('data-unit-label');
      const newUnitLabelSingular = newQuantitySelector?.getAttribute('data-unit-label-singular');
      const newUnitLabelOther = newQuantitySelector?.getAttribute('data-unit-label-other');

      if (newUnitLabel) {
        this.refs.quantitySelector.setAttribute('data-unit-label', newUnitLabel);
      } else {
        this.refs.quantitySelector.removeAttribute('data-unit-label');
      }

      if (newUnitLabelSingular) {
        this.refs.quantitySelector.setAttribute('data-unit-label-singular', newUnitLabelSingular);
      } else {
        this.refs.quantitySelector.removeAttribute('data-unit-label-singular');
      }

      if (newUnitLabelOther) {
        this.refs.quantitySelector.setAttribute('data-unit-label-other', newUnitLabelOther);
      } else {
        this.refs.quantitySelector.removeAttribute('data-unit-label-other');
      }
    }

    // Placed ahead of the add-to-cart-button early return further down, so it
    // still runs even for a variant with no visible add-to-cart button.
    this.#updateDeliverySplitNote();

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state. A guest-gated visitor stays disabled
    // regardless of the new variant's stock — availability isn't why their
    // button is disabled, so it must never be re-enabled based on it.
    if (event.detail.resource == null || event.detail.resource.available == false || this.dataset.guestGated === 'true') {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false || this.dataset.guestGated === 'true') {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    // Morph the backorder/"contact us" note callout wrapper so it reflects the newly
    // selected variant's inventory state. The wrapper is always rendered (even empty),
    // so unlike the note elements themselves it's never added/removed — it can be
    // morphed directly instead of guessing where to insert/remove a sibling note.
    const currentNoteCallout = this.refs.productNoteCallout;
    const newNoteCallout = event.detail.data.html.querySelector('[ref="productNoteCallout"]');
    if (currentNoteCallout instanceof Element && newNoteCallout) {
      morph(currentNoteCallout, newNoteCallout);
    }

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
